/**
 * Blog Page
 * ---------
 * Layout:
 *   1. Page header with intro
 *   2. Featured post — large card at the top
 *   3. Category filter tabs
 *   4. Blog post grid — remaining posts in a responsive grid
 *   5. Newsletter CTA at the bottom
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import BlogCard from '../../components/BlogCard/BlogCard';
import { blogPosts, blogCategories, getCategoryColor } from '../../data/blogPosts';
import styles from './Blog.module.css';

/* Separate the featured post (first item) from the rest */
const featuredPost    = blogPosts[0];
const remainingPosts  = blogPosts.slice(1);

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  /* Filter the non-featured posts by category */
  const filteredPosts = useMemo(() => {
    if (activeCategory === 'All') return remainingPosts;
    if (activeCategory === featuredPost.category) {
      /* Include the featured post in the grid when its category is selected */
      return blogPosts.filter(p => p.category === activeCategory);
    }
    return remainingPosts.filter(p => p.category === activeCategory);
  }, [activeCategory]);

  const featuredColors = getCategoryColor(featuredPost.category);

  return (
    <main className={styles.main}>

      {/* ══════════════════════════════════════
          PAGE HEADER
      ══════════════════════════════════════ */}
      <section className={styles.pageHeader}>
        <div className={styles.headerGlow} aria-hidden="true" />
        <div className={styles.container}>
          <AnimatedSection className={styles.headerContent}>
            <span className={styles.breadcrumb}>Community writing</span>
            <h1 className={styles.pageTitle}>
              Stories from the{' '}
              <span className={styles.titleAccent}>lab</span>
            </h1>
            <p className={styles.pageIntro}>
              Essays, reflections, and hard-won lessons from scientists who are doing the work and writing about it honestly. No sponsored content, no curated success stories.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FEATURED POST
      ══════════════════════════════════════ */}
      {activeCategory === 'All' && (
        <section className={styles.featuredSection}>
          <div className={styles.container}>
            <AnimatedSection>
              <div className={styles.featuredLabel}>
                <svg viewBox="0 0 16 16" fill="currentColor" className={styles.featuredIcon}>
                  <path d="M8 1l1.854 3.756L14 5.528l-3 2.924.708 4.129L8 10.396l-3.708 2.185L5 8.452 2 5.528l4.146-.772z"/>
                </svg>
                Editor's pick
              </div>

              <article className={styles.featuredCard}>
                {/* Left: text */}
                <div className={styles.featuredText}>
                  <span
                    className={styles.featuredCategory}
                    style={{ background: featuredColors.bg, color: featuredColors.text }}
                  >
                    {featuredPost.category}
                  </span>
                  <h2 className={styles.featuredTitle}>{featuredPost.title}</h2>
                  <p className={styles.featuredExcerpt}>{featuredPost.excerpt}</p>

                  <div className={styles.featuredMeta}>
                    <div className={styles.featuredAuthor}>
                      <div
                        className={styles.featuredAvatar}
                        style={{
                          background: `${featuredPost.author.color}20`,
                          color: featuredPost.author.color,
                        }}
                      >
                        {featuredPost.author.initials}
                      </div>
                      <div>
                        <div className={styles.featuredAuthorName}>{featuredPost.author.name}</div>
                        <div className={styles.featuredAuthorField}>{featuredPost.author.field}</div>
                      </div>
                    </div>
                    <div className={styles.featuredReadMeta}>
                      <span>{featuredPost.readingTime}</span>
                      <span className={styles.dot}>·</span>
                      <time>{featuredPost.date}</time>
                    </div>
                  </div>

                  <Link to={`/blog/${featuredPost.slug}`} className={styles.readBtn} aria-label={`Read: ${featuredPost.title}`}>
                    Read article
                    <svg viewBox="0 0 20 20" fill="none">
                      <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Link>
                </div>

                {/* Right: decorative gradient panel */}
                <div
                  className={styles.featuredVisual}
                  aria-hidden="true"
                  style={{ background: 'linear-gradient(135deg, #052e16 0%, #14532d 40%, #0a1628 100%)' }}
                >
                  <div className={styles.featuredQuote}>
                    "My first instinct was to hide the data. My second instinct was to delete it. Instead, I posted it on Labora the same afternoon."
                  </div>
                </div>
              </article>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════
          FILTER + GRID
      ══════════════════════════════════════ */}
      <section className={styles.gridSection}>
        <div className={styles.container}>

          {/* Category filters */}
          <AnimatedSection className={styles.filters}>
            <div className={styles.filterScroll}>
              {blogCategories.map((cat) => (
                <button
                  key={cat}
                  className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterBtnActive : ''}`}
                  onClick={() => setActiveCategory(cat)}
                  aria-pressed={activeCategory === cat}
                >
                  {cat}
                </button>
              ))}
            </div>
          </AnimatedSection>

          {/* Result count */}
          <div className={styles.resultMeta}>
            <span className={styles.resultCount}>
              {activeCategory === 'All'
                ? `${remainingPosts.length} articles`
                : `${filteredPosts.length} article${filteredPosts.length !== 1 ? 's' : ''} in ${activeCategory}`}
            </span>
          </div>

          {/* Grid */}
          {filteredPosts.length > 0 ? (
            <div className={styles.blogGrid}>
              {filteredPosts.map((post, i) => (
                <AnimatedSection key={post.id} delay={Math.min(i * 60, 280)}>
                  <BlogCard post={post} />
                </AnimatedSection>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📝</div>
              <h3 className={styles.emptyTitle}>No articles in this category yet</h3>
              <p className={styles.emptyText}>The community is always writing. Check back soon.</p>
            </div>
          )}

        </div>
      </section>

      {/* ══════════════════════════════════════
          NEWSLETTER CTA
      ══════════════════════════════════════ */}
      <section className={styles.newsletterSection}>
        <div className={styles.container}>
          <AnimatedSection className={styles.newsletterCard}>
            <div className={styles.newsletterText}>
              <h2 className={styles.newsletterTitle}>Get the best articles in your inbox</h2>
              <p className={styles.newsletterSub}>
                We send a weekly digest of the best Labora writing — no noise, no ads, just honest science writing. Unsubscribe any time.
              </p>
            </div>
            <form className={styles.newsletterForm} onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="your@email.com"
                className={styles.newsletterInput}
                aria-label="Email address"
              />
              <button type="submit" className={styles.newsletterBtn}>
                Subscribe
              </button>
            </form>
          </AnimatedSection>
        </div>
      </section>

    </main>
  );
};

export default Blog;
